import React, { useEffect, useRef } from "react";
import "./styles/App.scss";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import Amplify from "aws-amplify";
import * as amplitude from "@amplitude/analytics-browser";
import LoginPage from "./views/login/LoginPage";
import {
  AMPLITUDE_KEY,
  AWS_CONFIG,
  ENABLE_MONITOR_MODE,
} from "./configs/app-config";
import { useAppDispatch, useAppSelector } from "./redux/hooks";
import { isLoggedIn } from "./views/login/actions";
import { initApi } from "./utils/apiUtils";
import applyTheme from "./styles/theme";
import SignUp from "./views/signup/SignUp";
import EmailVerify from "./views/signup/EmailVerify";
import Main from "./views/main/Main";
import { ROUTES } from "./constants";
import { notifyError } from "./utils/notification";
import Loading from "./components/Loading";
import MonitoringJobListViewItemView from "./views/monitor/MonitoringJobListViewItemView";
import AnalyticsView from "./views/analytics/AnalyticsView";

Amplify.configure(AWS_CONFIG);
initApi();

function App() {
  const userId = useAppSelector((state: any) => state.userData.userId);
  const dispatch = useAppDispatch();
  const [isInitializing, setIsInitializing] = React.useState(true);

  // Prevent double effect in React 18 StrictMode
  const didRun = useRef(false);

  const loadAmplitude = () => {
    amplitude.init(AMPLITUDE_KEY, undefined, {
      defaultTracking: {
        sessions: true,
        pageViews: true,
        formInteractions: true,
        fileDownloads: true,
      },
    });
  };

  useEffect(() => {
    if (didRun.current) return;
    didRun.current = true;
    const errorDescriptionKeyAndValue = window.location.search;
    const urlSerarchParams = new URLSearchParams(errorDescriptionKeyAndValue);
    const googleSignInError = urlSerarchParams.get("error_description");
    if (googleSignInError) {
      notifyError(googleSignInError, "google_sign_in_error", 5000);
    }
    isLoggedIn(dispatch).finally(() => setIsInitializing(false));
    loadAmplitude();
    applyTheme();
  }, [dispatch]);

  const redirectLogin = (location: any) => (
    <Navigate to={ROUTES.LOGIN} state={{ from: location.pathname }} replace />
  );

  if (isInitializing) {
    return <Loading plain size="large" />;
  }

  return (
    <div className="fp_pg_full fp_base_container">
      <Routes>
        <Route
          path={`${ROUTES.SHARE_MONITOR}/:shareId`}
          element={<MonitoringJobListViewItemView />}
        />
        <Route
          path={`${ROUTES.SHARE_ANALYTICS}/:shareId`}
          element={<AnalyticsView />}
        />
        {!userId && <Route path={ROUTES.LOGIN} element={<LoginPage />} />}
        {!ENABLE_MONITOR_MODE && !userId && (
          <Route path={ROUTES.SIGN_UP} element={<SignUp />} />
        )}
        {!ENABLE_MONITOR_MODE && !userId && (
          <Route path={ROUTES.VERIFY} element={<EmailVerify />} />
        )}
        {!userId && <Route path="*" element={redirectLogin(window.location)} />}
        {userId && <Route path="/*" element={<Main />} />}
      </Routes>
    </div>
  );
}

export default App;
